/**
 * Code from Vue 2 mixin now in a Vue 3 composable.
 *
 * @author crm4f
 */
import { onMounted, ref } from 'vue'

export const useMapCommon = () => {
  const featureLabels = ref([])
  const minX = 1023500
  const maxX = 1023602
  const minY = 654350
  const maxY = 654383
  
  onMounted(() => {
    console.log(featureLabels.value)
  })

  const addMarker = (map, google, name, lat, lng, directions, setFocus, showInfoWindow, icon) => {
    lat = Number(lat)
    lng = Number(lng)
    let info = '<strong>' + name + '</strong>'
    if (directions != null) {
      info = info + '<br/>' + directions
    }
    var infoWindow = new google.maps.InfoWindow({
      content: info
    })
    var marker = new google.maps.Marker({
      position: { lat, lng },
      map: map,
      title: name,
      info: name,
      icon: icon
    })
    marker.addListener('click', function () {
      infoWindow.open(map, marker)
    })
    if (setFocus) {
      map.setZoom(18)
      map.panTo(marker.getPosition())
    }
    if (showInfoWindow) {
      infoWindow.open(map, marker)
    }
    return marker
  }

  const clearFeatures = (map) => {
    map.data.forEach(function (feature) {
      map.data.remove(feature)
    })
    for (var x = 0; x < featureLabels.value.length; x++) {
      featureLabels.value[x].setMap(null)
    }
    featureLabels.value = []
  }

  const getBuildingGroupStyle = () => {
    return {
      strokeColor: '#9d2f2f',
      strokeWeight: 1,
      fillOpacity: 1,
      fillColor: '#f8f8f8'
    }
  }

  // Loads building overlay geo json data and (optionally) accessible routes
  const loadGeoJson = (map, showAccessibleRoutes) => {
    clearFeatures(map)
    // Import FeatureLabel, wait the render over buildings
    import('@/components/FeatureLabel.js')
      .then(FeatureLabel => {
        map.data.loadGeoJson(process.env.VUE_APP_WS_URL + '/map/features?showBuildings=true&showAccessibleRoutes=' + showAccessibleRoutes +
          '&showToilets=false&showFoodOutlets=false&showBikeRacks=false&showAttractions=false&showAtms=false', {},  (features) => {
          initFeatures(map, FeatureLabel, features)
        })
      })
  }

  // Initialises toolbar and geo json overlays
  const initOverlays = (map, google, mapToolbar=null) => {
    if (mapToolbar != null) {
      initToolbar(map, google, mapToolbar)
    }
    initGeoJson(map)
  }

  // Initialises the toolbar. See the MapToolbar component for more.
  const initToolbar = (map, google, mapToolbar) => { // removed location param
    map.controls[google.maps.ControlPosition.TOP_RIGHT].push(mapToolbar.$el.childNodes[0])
  }

  // Makes on-load adjustments to geo json features
  const initFeatures = (map, FeatureLabel, features) => {
    for (var x = 0; x < features.length; x++) {
      if (features[x].getGeometry().getType() === 'Polygon') {
        var offset
        if (features[x].getProperty('buildingName') === 'Main Building') {
          offset = { x: 0, y: 15 }
        }
        // Adjust show at zoom for mobile.
        if (Number(features[x].getProperty('showLabelAtZoom')) === 17) {
          features[x].setProperty('showLabelAtZoom', 18)
        }
        if (features[x].getProperty('featureType') === 'Building') {
          featureLabels.value.push(FeatureLabel.create(map, features[x], 'buildingName', offset))
        } else {
          featureLabels.value.push(FeatureLabel.create(map, features[x], 'buildingGroupName', offset))
        }
      }
    }
  }

  const getStyles = () => {
    return [
      { featureType: 'road', elementType: 'geometry', stylers: [ { lightness: 100 }, { visibility: 'simplified' } ] },
      { featureType: 'road', elementType: 'labels.text.fill', stylers: [ { color: '#575656' } ] },
      { featureType: 'road', elementType: 'labels.text.stroke', stylers: [ { color: '#FFFFFF' } ] },
      { featureType: 'poi', elementType: 'labels', stylers: [ { visibility: 'off' } ] } 
    ]
  }

  // Returns building overlay style
  const getBuildingStyle = (map, feature) => {
    var fillOpacity = 1
    var buildingName = feature.getProperty('buildingName')
    if (map.getZoom() >= 17 && map.getZoom() <= 21 && (buildingName === 'Main Building' || buildingName === 'Joseph Black Building')) {
      // Make selected building overlays transparent so the room map tiles underneath show.
      fillOpacity = 0
    }
    return {
      strokeColor: '#9d2f2f',
      strokeWeight: 1,
      fillOpacity: fillOpacity,
      fillColor: '#f8f8f8'
    }
  }
  
  // Intialises geo json overlay data
  // If a map's data style isn't specified, it uses the one defined in this file.
  const initGeoJson = (map, mapDataStyleCallback=setMapDataStyle) => {
    loadGeoJson(map, false)
    mapDataStyleCallback(map)
    // Reset the style on zoom change.
    map.addListener('zoom_changed', function () {
      mapDataStyleCallback(map)
    })
    // Show building and other overlay names on click.
    map.data.addListener('click', function (event) {
      var feature = event.feature
      var content = null
      if (feature.getProperty('featureType') === 'Building') {
        content = infoHeader(feature.getProperty('buildingName'))
      } else if (feature.getProperty('featureType') === 'BuildingGroup') {
        content = infoHeader(feature.getProperty('buildingGroupName'))
      } else {
        content = infoHeader(feature.getProperty('featureName'))
        if (feature.getProperty('featureInfo') != null) {
          content += '<div>' + feature.getProperty('featureInfo') + '</div>'
        }
      }
      var infowindow = new google.maps.InfoWindow({
        content: content,
        position: event.latLng,
        ariaLabel: "Building name",
      })
      infowindow.open(map)
    })
  }

  // Wraps the text in an html header.
  const infoHeader = (text) => {
    return '<strong>' + text + '</strong>'
  }

  // Set the style on geo json overlay data
  const setMapDataStyle = (map) => {
    map.data.setStyle((feature) => {
      var featureType = feature.getProperty('featureType')
      if (featureType === 'Building') {
        return getBuildingStyle(map, feature)
      } else if (featureType === 'BuildingGroup') {
        return getBuildingGroupStyle()
      } else if (featureType === 'Route') {
        return {
          strokeColor: '#01608d',
          strokeWeight: 4
        }
      }
    })
  }

  return {
   clearFeatures,
   getBuildingGroupStyle,
   loadGeoJson,
   initFeatures,
   initOverlays,
   addMarker,
   getStyles,
   getBuildingStyle,
   initGeoJson,
   infoHeader,
   minX,
   minY,
   maxX,
   maxY
  }
}
  